import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LogOut, MapPin, Clock, ChevronRight, ShieldCheck, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { StatusBadge, BuDot } from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { fmtSchedule, todayISO } from "@/lib/format";
import { cn } from "@/lib/utils";
import { BrandLogo } from "@/components/BrandLogo";

const TABS = [
  { id: "hoy", label: "Hoy", test: (o, t) => o.scheduled_date === t && !["pendiente_cierre"].includes(o.status) },
  { id: "proximas", label: "Próximas", test: (o, t) => ["asignada", "programada", "en_proceso", "nueva", "en_revision", "pendiente_programacion"].includes(o.status) && o.scheduled_date !== t },
  { id: "pendientes", label: "Pendientes", test: (o) => o.status === "pendiente" },
  { id: "revision", label: "En revisión", test: (o) => o.status === "pendiente_cierre" },
];

export const TechHeader = ({ children }) => {
  const { user, logout } = useAuth();
  return (
    <header className="sticky top-0 z-30 border-b border-border bg-background/95 px-4 pb-3 pt-[max(env(safe-area-inset-top),0.75rem)] backdrop-blur-xl">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="rounded-md bg-white px-2 py-1"><BrandLogo className="h-6 w-auto" testid="tech-logo" /></span>
          <span className="flex items-center gap-1 font-display text-base font-extrabold"><ShieldCheck className="h-4 w-4 text-primary" />OS</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="max-w-[140px] truncate text-xs text-muted-foreground" data-testid="tech-user-name">{user?.name}</span>
          <Button variant="ghost" size="icon" onClick={logout} data-testid="tech-logout-button"><LogOut className="h-4 w-4" /></Button>
        </div>
      </div>
      {children}
    </header>
  );
};

const OrderCard = ({ o, meta }) => (
  <Link to={`/tecnico/os/${o.id}`} data-testid={`tech-wo-card-${o.folio}`}
    className="fade-up block rounded-xl border border-border bg-card p-4 transition-transform active:scale-[0.98]">
    <div className="flex items-center justify-between gap-2">
      <span className="font-mono text-sm font-bold text-primary">{o.folio}</span>
      <StatusBadge status={o.status} />
    </div>
    <div className="mt-2 font-display text-lg font-semibold leading-tight">{o.client_name}</div>
    <div className="mt-1 flex items-center gap-2 text-sm text-muted-foreground"><BuDot meta={meta} code={o.business_unit_code} name={o.business_unit_name} /> · {o.service_type_name}</div>
    <div className="mt-3 flex items-start gap-1.5 text-sm text-muted-foreground"><MapPin className="mt-0.5 h-4 w-4 shrink-0" /><span className="line-clamp-2">{o.service_address || "Sin dirección"}</span></div>
    <div className="mt-3 flex items-center justify-between">
      <span className="flex items-center gap-1.5 font-mono text-xs"><Clock className="h-3.5 w-3.5 text-primary" />{fmtSchedule(o.scheduled_date, o.scheduled_time)}</span>
      <ChevronRight className="h-5 w-5 text-muted-foreground" />
    </div>
  </Link>
);

export default function TechHome() {
  const { meta } = useAuth();
  const [orders, setOrders] = useState(null);
  const [tab, setTab] = useState("hoy");
  const load = () => api.get("/work-orders", { params: { include_closed: false } }).then((r) => setOrders(r.data));
  useEffect(() => { load(); }, []);
  const today = todayISO();
  const current = TABS.find((t) => t.id === tab);
  const list = (orders || []).filter((o) => current.test(o, today));

  return (
    <div className="mx-auto min-h-screen max-w-md pb-10">
      <TechHeader>
        <div className="mt-3 flex gap-1.5 overflow-x-auto">
          {TABS.map((t) => {
            const n = (orders || []).filter((o) => t.test(o, today)).length;
            return (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tech-tab-${t.id}`}
                className={cn("h-10 shrink-0 rounded-full px-4 text-sm font-medium transition-colors", tab === t.id ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground")}>
                {t.label} <span className="ml-1 font-mono text-xs opacity-70">{n}</span>
              </button>
            );
          })}
        </div>
      </TechHeader>
      <div className="flex flex-wrap items-center justify-between gap-y-2 px-4 pt-5">
        <h1 className="font-display text-2xl font-bold">Mis órdenes</h1>
        <Link to="/tecnico/os/nueva" className="ml-auto mr-2 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground" data-testid="tech-new-wo-link">Nueva OS</Link>
        <Link to="/tecnico/levantamientos" className="mr-2 rounded-md border border-border px-3 py-1.5 text-sm" data-testid="tech-surveys-link">Levantamientos</Link>
        <Link to="/tecnico/inventario" className="mr-2 rounded-md border border-border px-3 py-1.5 text-sm" data-testid="tech-inventory-link">Mi inventario</Link>
        <Button variant="ghost" size="icon" onClick={load} data-testid="tech-refresh-button"><RefreshCw className="h-4 w-4" /></Button>
      </div>
      <div className="space-y-3 px-4 pt-3" data-testid="tech-wo-list">
        {orders && list.length === 0 && <p className="rounded-xl border border-dashed border-border py-12 text-center text-sm text-muted-foreground">Sin órdenes en esta sección</p>}
        {list.map((o) => <OrderCard key={o.id} o={o} meta={meta} />)}
      </div>
    </div>
  );
}
